import { Check } from "lucide-react";
import { Button, Card, Text, Tooltip, type ButtonProps } from "@marekl/adminer/components";
import { formatDateTime, pluralizeCzech } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import {
  useSetCompetitionStatus,
  type Competition,
  type CompetitionStatus,
} from "../../lib/queries/competitions";
import {
  COMPETITION_STATUS_LABELS,
  formatCompetitionDates,
} from "../../lib/competitionState";
import styles from "./CompetitionLifecycle.module.css";

// The one lifecycle control, shared by the competition overview and the
// settings screen, drawn as the pipeline in docs/mockups/competition-detail.html:
// where the competition stands, then one row saying what happens next with
// the button that does it, and the one-step-back path in the header, out
// of the way, since it is the rare correction rather than the next move.
//
// The steps are the stages a reader thinks in, not the stored statuses:
// Zveřejněno and the two derived entry phases are one stage, „Přihlašování",
// whose line says whether it is open or closed. Ohlášeno is optional, so it
// is drawn only while it is the current state: shown done on a competition
// that was published straight from the draft, it would claim a step nobody
// took. Archivováno is drawn the same way.

type StepState = "done" | "current" | "todo";

interface Step {
  key: string;
  label: string;
  sub?: string;
  state: StepState;
}

// Position of each stored status along the pipeline; the step keys below
// share the scale, so a step is done once the status is past it.
const ORDER: Record<CompetitionStatus, number> = {
  draft: 0,
  announced: 1,
  published: 2,
  running: 3,
  finished: 4,
  archived: 5,
};

function entriesLine(c: Competition): string {
  const count = `${c.entry_count} ${pluralizeCzech(c.entry_count, ["přihláška", "přihlášky", "přihlášek"])}`;
  if (ORDER[c.status] < ORDER.published) {
    return c.entry_deadline ? `do ${formatDateTime(c.entry_deadline)}` : "Bez uzávěrky";
  }
  if (c.status === "published" && c.entry_phase === "open") {
    return c.entry_deadline ? `do ${formatDateTime(c.entry_deadline)} · ${count}` : count;
  }
  if (c.entry_phase === "closed_capacity") return `Kapacita naplněna · ${count}`;
  return `Přihlášky uzavřeny · ${count}`;
}

function buildSteps(c: Competition): Step[] {
  const at = ORDER[c.status];
  const state = (pos: number): StepState => (at > pos ? "done" : at === pos ? "current" : "todo");
  const steps: Step[] = [
    {
      key: "draft",
      label: COMPETITION_STATUS_LABELS.draft,
      sub: at === 0 ? "Týmy soutěž nevidí" : undefined,
      state: state(0),
    },
  ];
  if (c.status === "announced") {
    steps.push({ key: "announced", label: COMPETITION_STATUS_LABELS.announced, sub: "Bez přihlašování", state: "current" });
  }
  steps.push(
    { key: "entries", label: "Přihlašování", sub: entriesLine(c), state: state(2) },
    { key: "running", label: COMPETITION_STATUS_LABELS.running, sub: formatCompetitionDates(c), state: state(3) },
    { key: "finished", label: COMPETITION_STATUS_LABELS.finished, state: state(4) },
  );
  if (c.status === "archived") {
    steps.push({ key: "archived", label: COMPETITION_STATUS_LABELS.archived, state: "current" });
  }
  return steps;
}

interface Move {
  to: CompetitionStatus;
  label: string;
  description: string;
  /** Set when the server would refuse it: the button is disabled and
   *  this is its tooltip. */
  disabledReason?: string;
}

const PUBLISH: Move = {
  to: "published",
  label: "Zveřejnit soutěž",
  description: "Soutěž se zveřejní týmům a do uzávěrky bude otevřené přihlašování.",
};
const ANNOUNCE: Move = {
  to: "announced",
  label: "Ohlásit soutěž",
  description:
    "Týmy soutěž uvidí, ale přihlásit se na ni zatím nemohou. Uzávěrka přihlášek ani aktivní sada pravidel nejsou potřeba a týmům se nic neposílá.",
};
const START: Move = {
  to: "running",
  label: "Zahájit soutěž",
  description:
    "Soutěž se označí jako probíhající. Přihlašování se tím pro týmy definitivně uzavře.",
};
const FINISH: Move = {
  to: "finished",
  label: "Ukončit soutěž",
  description:
    "Soutěž se označí jako dokončená a zamkne se celá: výsledky platí, přihlášky, startovní listina ani nastavení se už nezmění a zpět do stavu Probíhá vrátit nejde.",
};
const ARCHIVE: Move = {
  to: "archived",
  label: "Archivovat soutěž",
  description: "Soutěž se archivuje a bude pouze pro čtení.",
};

const NO_DEADLINE =
  "Zveřejněním se otevře přihlašování, a to potřebuje uzávěrku přihlášek. Nejdřív ji vyplňte v nastavení soutěže.";

// A disabled button fires no pointer events, so the tooltip saying why it
// is disabled sits on a wrapping span (the library's documented pattern).
function MoveButton({
  move,
  onPick,
  ...rest
}: { move: Move; onPick: (m: Move) => void } & Omit<ButtonProps, "onClick" | "disabled">) {
  const button = (
    <Button {...rest} disabled={!!move.disabledReason} onClick={() => onPick(move)}>
      {move.label}
    </Button>
  );
  if (!move.disabledReason) return button;
  return (
    <Tooltip content={move.disabledReason}>
      <span>{button}</span>
    </Tooltip>
  );
}

interface Next {
  /** What is true now and what the button would change, in one breath. */
  text: string;
  /** The main move first; a second one is the quieter alternative. */
  moves: Move[];
  /** False when the main move is not the thing to do yet. */
  urgent: boolean;
}

function nextStep(c: Competition): Next {
  const noDeadline = !c.entry_deadline;
  switch (c.status) {
    case "draft":
      return {
        text: noDeadline
          ? "Týmy soutěž zatím nevidí. Ke zveřejnění chybí uzávěrka přihlášek; ohlásit ji jde hned."
          : "Týmy soutěž zatím nevidí. Zveřejněním otevřete přihlašování, ohlášením ji jen ukážete.",
        moves: noDeadline ? [ANNOUNCE, { ...PUBLISH, disabledReason: NO_DEADLINE }] : [PUBLISH, ANNOUNCE],
        urgent: true,
      };
    case "announced":
      return {
        text: noDeadline
          ? "Týmy soutěž vidí, ale přihlásit se nemohou. Před zveřejněním nastavte uzávěrku přihlášek."
          : "Týmy soutěž vidí, ale přihlásit se nemohou. Zveřejněním otevřete přihlašování.",
        moves: [noDeadline ? { ...PUBLISH, disabledReason: NO_DEADLINE } : PUBLISH],
        urgent: true,
      };
    case "published":
      return c.entry_phase === "open"
        ? {
            text: "Přihlašování běží. Soutěž zahájíte v den konání.",
            moves: [START],
            urgent: false,
          }
        : {
            text: "Přihlášky jsou uzavřené. V den konání soutěž zahajte.",
            moves: [START],
            urgent: true,
          };
    case "running":
      return { text: "Soutěž probíhá. Po vyhlášení výsledků ji ukončete.", moves: [FINISH], urgent: true };
    case "finished":
      return { text: "Soutěž je dokončená a výsledky platí.", moves: [ARCHIVE], urgent: false };
    case "archived":
      return { text: "Soutěž je archivovaná a je pouze pro čtení.", moves: [], urgent: false };
  }
}

const BACK: Partial<Record<CompetitionStatus, { to: CompetitionStatus; description: string }>> = {
  announced: {
    to: "draft",
    description: "Soutěž se vrátí do přípravy a týmům se přestane zobrazovat.",
  },
  published: {
    to: "announced",
    description:
      "Přihlašování se zavře, týmy ale soutěž dál uvidí. Jde to jen, dokud nikdo nepodal přihlášku.",
  },
  running: {
    to: "published",
    description:
      "Soutěž se vrátí do stavu Zveřejněno; přihlašování se znovu řídí uzávěrkou.",
  },
  // No way back from Dokončeno (R20): the results are official and
  // postup is computed from them. The server refuses it too.
  archived: {
    to: "finished",
    description: "Soutěž se vrátí do stavu Dokončeno.",
  },
};

export function CompetitionLifecycle({
  competition,
  readOnly = false,
}: {
  competition: Competition;
  /** The competitions_viewer: the track and what comes next are shown, the
   * moves that change the status are not. */
  readOnly?: boolean;
}) {
  const setStatus = useSetCompetitionStatus();
  const confirm = useConfirm();

  const steps = buildSteps(competition);
  const next = nextStep(competition);
  const back = BACK[competition.status];

  // The dialog stays open on a refusal and shows it there, so a transition
  // that fails never needs the card to report it.
  const askTransition = (to: CompetitionStatus, title: string, description: string, danger: boolean) =>
    confirm({
      title,
      description: (
        <>
          {description} Stav se změní na <strong>{COMPETITION_STATUS_LABELS[to]}</strong>.
        </>
      ),
      confirmLabel: "Potvrdit",
      variant: danger ? "danger" : "primary",
      onConfirm: async () => {
        await setStatus.mutateAsync({ id: competition.id, status: to });
      },
    });

  const pick = (m: Move) => askTransition(m.to, m.label, m.description, false);

  return (
    <Card>
      <Card.Header
        title="Průběh soutěže"
        actions={
          back && !readOnly && (
            <Button
              size="sm"
              weight="ghost"
              onClick={() =>
                askTransition(back.to, "Vrátit do předchozího stavu", back.description, true)
              }
            >
              Vrátit do předchozího stavu
            </Button>
          )
        }
      />
      <Card.Content>
        <div className={styles.track}>
        <ol className={styles.pipeline} aria-label="Průběh soutěže">
          {steps.map((step, i) => (
            <li
              key={step.key}
              className={styles.step}
              data-state={step.state}
              aria-current={step.state === "current" ? "step" : undefined}
            >
              {/* The done marker is a lucide glyph, not „✓": every glyph in
                  the UI is a stroked icon (docs/mockups/DESIGN.md §3). */}
              <span className={styles.dot} aria-hidden="true">
                {step.state === "done" ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span className={styles.label}>
                <span className={styles.title}>{step.label}</span>
                {step.sub && <span className={styles.sub}>{step.sub}</span>}
              </span>
            </li>
          ))}
        </ol>
        </div>
      </Card.Content>
      <Card.Footer>
        <div className={styles.next}>
          <Text variant="muted">{next.text}</Text>
          {!readOnly && next.moves.length > 0 && (
            <div className={styles.actions}>
              {/* Quieter alternative first, so the main move ends the row. */}
              {next.moves
                .slice(1)
                .reverse()
                .map((m) => (
                  <MoveButton key={m.to} move={m} onPick={pick} weight="outline" />
                ))}
              <MoveButton
                move={next.moves[0]}
                onPick={pick}
                variant={next.urgent ? "primary" : "neutral"}
                weight={next.urgent ? "solid" : "outline"}
              />
            </div>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
}
